import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  TextField,
  Autocomplete,
  Alert,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';

const ImportDiplomaDialog = ({
  open,
  onClose,
  templates,
  selectedTemplate,
  setSelectedTemplate,
  selectedFile,
  setSelectedFile,
  handleImport,
  filterTemplateYear,
  setFilterTemplateYear,
  filterTemplateType,
  setFilterTemplateType,
  templateYears,
  templateTypes,
  getTemplateImageUrl,
  yearWarning,
  loading = false,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState('');

  const diplomaTypeLabels = {
    '1': t('diplomaTemplate.zlatna'),
    '2': t('diplomaTemplate.prviRang'),
    '3': t('diplomaTemplate.finalniRang'),
    'final rang': t('diplomaTemplate.finalniRang'),
  };

  useEffect(() => {
    if (!open) setFileError('');
  }, [open]);

  const getTemplateTypeLabel = (type) => {
    const key = String(type || '').trim();
    return diplomaTypeLabels[key] || type || 'Unknown';
  };

  const isFinalRang = selectedTemplate &&
    (String(selectedTemplate.type) === '3' || selectedTemplate.type === 'final rang');

  const handleFileSelectClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const validExtensions = ['.xlsx', '.xls'];
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      setFileError(t('importDiploma.fileTypeError'));
      setSelectedFile(null);
      event.target.value = '';
      return;
    }

    setFileError('');
    setSelectedFile(file);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { position: 'relative', overflow: 'hidden' } }}>

      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            borderRadius: 'inherit',
          }}
        >
          <CircularProgress size={56} thickness={4} sx={{ color: '#1976d2' }} />
          <Typography variant="body1" fontWeight={700} sx={{ color: '#333' }}>
            {t('importDiploma.generating')}…
          </Typography>
          <Typography variant="caption" sx={{ color: '#777', textAlign: 'center', px: 4 }}>
            {t('loading.description')}
          </Typography>
        </Box>
      )}

      <DialogTitle>{t('importDiploma.title')}</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('importDiploma.fileWarning')}
          </Typography>
          <Typography variant="body2">
            {t('importDiploma.fileInstruction')}
          </Typography>
        </Box>

        {yearWarning && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2, borderRadius: '8px', border: '1px solid #ffc107' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('importDiploma.yearConflictTitle', { year: yearWarning.year, type: yearWarning.type })}
            </Typography>
            <Typography variant="body2">
              {t('importDiploma.yearConflictBody', { count: yearWarning.count })}
            </Typography>
          </Alert>
        )}

        {isFinalRang && (
          <Box sx={{
            mb: 2, p: 2,
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
          }}>
            <Typography variant="body2" sx={{ color: '#856404', fontWeight: 'bold', mb: 0.5 }}>
              {t('importDiploma.finalRangWarningTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#856404' }}>
              {t('importDiploma.finalRangWarningBody')}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={templateYears}
            value={filterTemplateYear || null}
            onChange={(_, newValue) => setFilterTemplateYear(newValue || 'All')}
            getOptionLabel={(option) => option === 'All' ? t('general.all') : option.toString()}
            renderInput={(params) => <TextField {...params} label={t('diplomaTemplate.year')} />}
            sx={{ minWidth: 140 }}
          />
          <Autocomplete
            size="small"
            options={templateTypes}
            value={filterTemplateType || null}
            onChange={(_, newValue) => setFilterTemplateType(newValue || 'All')}
            getOptionLabel={(option) => option === 'All' ? t('general.all') : option.toString()}
            renderInput={(params) => <TextField {...params} label={t('diplomaTemplate.type')} />}
            sx={{ minWidth: 220 }}
          />
        </Box>

        <Grid container spacing={2}>
          {templates
            .filter((t) => filterTemplateYear === 'All' || String(t.year) === filterTemplateYear)
            .filter((t) => filterTemplateType === 'All' || getTemplateTypeLabel(t.type) === filterTemplateType)
            .map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Card
                  onClick={() => !loading && setSelectedTemplate(template)}
                  sx={{
                    cursor: loading ? 'default' : 'pointer',
                    border: selectedTemplate?.id === template.id
                      ? '2px solid #4cceac'
                      : '1px solid #ddd',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: loading ? 0 : 4 },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="140"
                    image={getTemplateImageUrl(template)}
                    alt={template.name}
                  />
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1a237e' }}>
                      {template.name}
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'block', color: '#333', mb: 0.5, fontWeight: '600' }}>
                      {t('diplomaTemplate.yearLabel')} {template.year || 'N/A'}
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'block', color: '#333', fontWeight: '600' }}>
                      {t('diplomaTemplate.typeLabel')} {getTemplateTypeLabel(template.type)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={handleFileSelectClick}
            variant="contained"
            disabled={!selectedTemplate || loading}
            title={!selectedTemplate ? t('importDiploma.selectTemplateFirst') : ''}
            sx={{
              backgroundColor: selectedTemplate && !loading ? '#1976d2' : '#bdbdbd',
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            {t('importDiploma.selectFile')}
          </Button>

          {!selectedTemplate && (
            <Typography variant="caption" sx={{ color: '#d32f2f' }}>
              {t('importDiploma.selectTemplateFirst')}
            </Typography>
          )}

          {fileError && (
            <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
              {fileError}
            </Typography>
          )}

          {selectedFile && (
            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
              ✓ {selectedFile.name}
            </Typography>
          )}

          <input
            type="file"
            ref={fileInputRef}
            hidden
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button onClick={onClose} disabled={loading}>{t('dialog.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!selectedFile || !selectedTemplate || loading}
            sx={{
              backgroundColor: (!selectedFile || !selectedTemplate || loading) ? '#bdbdbd' : (yearWarning ? '#e65100' : '#4cceac'),
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: '8px',
              minWidth: 160,
              '&:hover': {
                backgroundColor: (!selectedFile || !selectedTemplate || loading)
                  ? '#bdbdbd'
                  : (yearWarning ? '#bf360c' : '#2ea87a'),
              },
            }}
          >
            {loading
              ? <CircularProgress size={20} thickness={4} sx={{ color: '#fff' }} />
              : yearWarning ? t('importDiploma.replaceAndImport') : t('importDiploma.import')
            }
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDiplomaDialog;